import React, { useState } from 'react'

import { useCreateDevices } from '../hooks/useCreateDevices'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { useForm } from 'react-hook-form'
import { HiOutlineCheckCircle } from 'react-icons/hi'

import {
  Box,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@chakra-ui/react'

import { Button } from '@/components/ui'
import {
  useCreateAlarmMutation,
  useCreateCameraMutation,
  useCreateDoorMutation,
} from '@/graphql/generated/hooks'
import {
  CreateAlarmInput,
  CreateCameraInput,
  CreateDoorInput,
  DeviceType,
} from '@/graphql/generated/schemas'

import { DeviceModalHeader } from '../../components/DeviceModalHeader'
import { FormInputsIProps } from '../types/types'
import { CSV_UploadStatus } from '../utils/csvParser'
import { CSVParseResultI, VALID_DEVICES } from '../utils/csvValidator'
import { BulkUploadForm } from './BulkUploadForm'
import { CreateDeviceForm } from './CreateDeviceForm'

interface IProps {
  isOpen: boolean
  onClose: () => void
}

export const CreateDeviceModal = ({ isOpen, onClose }: IProps) => {
  const { shouldEnableCreateDevice } = usePermissions()
  const isUpdatedBulkUpload = useFeatureFlag(
    FeatureFlag.updatedDeviceBulkUpload
  )
  const form = useForm<FormInputsIProps>({ mode: 'onChange' })
  const { handleSubmit, watch, formState } = form
  const [parseResult, setParseResult] = useState<CSVParseResultI | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [isParsing, setIsParsing] = useState(false)
  const [bulkUploadResetKey, setBulkUploadResetKey] = useState(0)
  const [createAlarm] = useCreateAlarmMutation()
  const [createDoor] = useCreateDoorMutation()
  const [createCamera] = useCreateCameraMutation()
  const { showSuccess, showError } = useToasts({ preventDuplicate: false })

  const { isValid } = formState
  const [isBulkUploadSelected, setIsBulkUploadSelected] = useState(false)
  const {
    create,
    setFacilityName,
    facilityOptions,
    floorOptions,
    isFacilitiesLoading,
    isOptionsLoading,
    isLoading,
  } = useCreateDevices({
    onClose,
    facilityId: watch('facility')?.value,
    isBulkUploadSelected,
  })

  const pendingRows =
    parseResult?.data.filter(
      (d) =>
        d.uploadStatus === CSV_UploadStatus.Pending || d.uploadStatus == null
    ) ?? []

  const isBulkUploadActive = isUpdatedBulkUpload && isBulkUploadSelected
  const isBulkFormInvalid = !parseResult || pendingRows.length === 0
  const isFormInvalid = isBulkUploadActive ? isBulkFormInvalid : !isValid
  const isSubmitDisabled = !shouldEnableCreateDevice || isFormInvalid
  const isBulkBusy = isCreating || isParsing
  const isBusy = isBulkUploadActive ? isBulkBusy : isLoading

  interface CreateDeviceInputBaseI {
    name: string
    externalId?: string
    floorId: string
    label?: string
  }

  const handleBulkCreate = async () => {
    if (!parseResult || pendingRows.length === 0) {
      showError('No devices to create. Please upload a valid CSV file.')
      return
    }
    setIsCreating(true)
    const results = {
      successful: [] as string[],
      failed: [] as { name: string; error: string }[],
    }
    const devicesWithStatus = parseResult.data.map((device) => ({
      ...device,
      uploadStatus:
        device.uploadStatus === CSV_UploadStatus.Failed ||
        device.uploadStatus === CSV_UploadStatus.Success
          ? device.uploadStatus
          : CSV_UploadStatus.Pending,
    }))
    setParseResult({ ...parseResult, data: devicesWithStatus })
    const updatedDevices = [...devicesWithStatus]

    for (let i = 0; i < updatedDevices.length; i++) {
      const device = updatedDevices[i]
      if (device.uploadStatus !== CSV_UploadStatus.Pending) continue
      const base: CreateDeviceInputBaseI = {
        name: device.name,
        floorId: device.floorId,
        externalId: device.externalId,
        label: device.label,
      }
      try {
        const refetchQueries = [
          'Devices',
          'FloorDevicesCount',
          'FacilityDevicesByFloors',
        ]
        switch (device.deviceType) {
          case DeviceType.Alarm: {
            const alarmInput: CreateAlarmInput = { ...base }
            await createAlarm({
              variables: { alarmInput },
              refetchQueries: refetchQueries,
            })
            break
          }
          case DeviceType.Door: {
            const doorInput: CreateDoorInput = { ...base }
            await createDoor({
              variables: { doorInput },
              refetchQueries: refetchQueries,
            })
            break
          }
          case DeviceType.Camera: {
            const cameraInput: CreateCameraInput = {
              ...base,
              macAddress: device.macAddress,
              ip: device.ip,
              rtspPath: device.rtspPath,
              rtspPort: device.rtspPort,
              streamingProtocol: device.streamProtocol,
              streamingType: device.streamType,
            }
            await createCamera({
              variables: { cameraInput },
              refetchQueries: refetchQueries,
            })
            break
          }
          default:
            throw Error(
              `Device Type: ${device.deviceType} is not one of ${VALID_DEVICES}`
            )
        }
        results.successful.push(device.name)
        updatedDevices[i] = {
          ...device,
          uploadStatus: CSV_UploadStatus.Success,
        }
      } catch (e) {
        results.failed.push({ name: device.name, error: e.message })
        updatedDevices[i] = {
          ...device,
          uploadStatus: CSV_UploadStatus.Failed,
          uploadError: e.message,
        }
      }
      setParseResult((prev) => ({ ...prev, data: [...updatedDevices] }))
    }

    setIsCreating(false)

    if (results.successful.length > 0) {
      const count = results.successful.length
      showSuccess(
        `Successfully created ${count} ${count === 1 ? 'device' : 'devices'}`
      )
    }

    const totalFailed = updatedDevices.filter(
      (d) => d.uploadStatus === CSV_UploadStatus.Failed
    ).length
    if (totalFailed > 0) {
      showError(
        `Failed to create ${totalFailed} ${
          totalFailed === 1 ? 'device' : 'devices'
        }. View results in table.`
      )
    }
  }

  const handleClose = () => {
    if (!isBusy) {
      onClose()
    }
  }

  const handleFormSubmit = async () => {
    if (isBulkUploadActive) {
      await handleBulkCreate()
    } else {
      await handleSubmit(create)()
    }
  }

  return (
    <Modal
      closeOnOverlayClick={!isBusy}
      id='createDeviceModal'
      isCentered
      isOpen={isOpen}
      motionPreset='slideInBottom'
      onClose={handleClose}
      scrollBehavior='inside'
      size='xl'
    >
      <ModalOverlay />
      <ModalContent
        bg='#fff'
        maxW={
          isUpdatedBulkUpload && parseResult?.data?.length ? '90%' : undefined
        }
        rounded='20px'
      >
        <ModalCloseButton color='#000' isDisabled={isBusy} opacity={0.4} />
        <DeviceModalHeader title='Create Device' />
        <ModalBody p='0'>
          <Tabs
            bgColor='#FFFFFF'
            borderColor='transparent'
            fontWeight='500'
            isLazy
            onChange={(index) => {
              setIsBulkUploadSelected(index !== 0)
              if (isUpdatedBulkUpload && !isCreating) {
                setParseResult(null)
                form.setValue('file', null)
                setBulkUploadResetKey((k) => k + 1)
              }
            }}
            px='10'
          >
            <TabList>
              <Tab
                _selected={{
                  color: 'components.tab.active',
                  borderBottomColor: 'components.tab.active',
                  opacity: 1,
                }}
                isDisabled={isBusy}
                opacity={0.4}
              >
                <Box alignItems='center' display='inline-flex'>
                  Create Individual
                </Box>
              </Tab>
              <Tab
                _selected={{
                  color: 'components.tab.active',
                  borderBottomColor: 'components.tab.active',
                  opacity: 1,
                }}
                isDisabled={isBusy}
                opacity={0.4}
              >
                <Box alignItems='center' display='inline-flex'>
                  Bulk Upload
                </Box>
              </Tab>
            </TabList>
            <TabPanels>
              <TabPanel mt='5px' px='0'>
                <CreateDeviceForm
                  facilityOptions={facilityOptions}
                  floorOptions={floorOptions}
                  form={form}
                  isFacilitiesLoading={isFacilitiesLoading}
                  isOptionsLoading={isOptionsLoading}
                  setFacilityName={setFacilityName}
                />
              </TabPanel>
              <TabPanel mt='5px' px='0'>
                <BulkUploadForm
                  form={form}
                  isRemoveDisabled={isCreating || isParsing}
                  key={isUpdatedBulkUpload ? bulkUploadResetKey : undefined}
                  onIsParsingChange={
                    isUpdatedBulkUpload ? setIsParsing : undefined
                  }
                  onParseResultChange={
                    isUpdatedBulkUpload ? setParseResult : undefined
                  }
                  parseResult={isUpdatedBulkUpload ? parseResult : undefined}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
        <ModalFooter py='4'>
          <Button
            isDisabled={isBusy}
            mr='3'
            onClick={handleClose}
            variant='secondary'
          >
            Cancel
          </Button>
          <Button
            isDisabled={isSubmitDisabled}
            isLoading={isBusy}
            leftIcon={<HiOutlineCheckCircle size='24px' />}
            loadingText='Creating Devices'
            onClick={handleFormSubmit}
          >
            Create Device
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
